import React from "react";
import { Listbox } from "@headlessui/react";

interface Branch {
  id: number;
  name: string;
}

interface BranchSelectProps {
  branches: Branch[];
  selectedBranch: number | null;
  onBranchChange: (branchId: number | null) => void;
  isLoading?: boolean;
  showAllBranches?: boolean;
  allBranchesValue?: string;
  className?: string;
  placeholder?: string;
  fullWidth?: boolean;
}

const BranchSelect: React.FC<BranchSelectProps> = ({
  branches,
  selectedBranch,
  onBranchChange,
  isLoading = false,
  showAllBranches = false,
  className = "w-full md:w-[324px] h-10 px-3 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white focus:outline-none",
  placeholder = "Select branch",
  fullWidth = false,
}) => {
  const selectedBranchObj =
    branches.find((branch) => branch.id === selectedBranch) || null;
  return (
    <div className={`flex flex-col gap-2 ${fullWidth ? "flex-1" : ""}`}>
      <Listbox
        value={
          !selectedBranchObj && showAllBranches
            ? {
                id: null,
                name: "All Branches",
              }
            : selectedBranchObj
        }
        onChange={(branch) => {
          onBranchChange(branch?.id || null);
        }}
      >
        <div className="relative">
          <Listbox.Button
            disabled={isLoading}
            className={`${className} ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span className="block truncate text-left">
              {isLoading
                ? "Loading branches..."
                : !selectedBranch && showAllBranches
                  ? "All Branches"
                  : selectedBranchObj?.name || placeholder}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg
                className="w-5 h-5 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </Listbox.Button>

          <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
            {showAllBranches && (
              <Listbox.Option
                key="all"
                value={null}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                  }`
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${
                        !selected && !selectedBranchObj
                          ? "font-medium"
                          : "font-normal"
                      }`}
                    >
                      All Branches
                    </span>
                    {!selected && !selectedBranchObj ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            )}
            {branches.map((branch) => (
              <Listbox.Option
                key={branch.id}
                value={branch}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                  }`
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                    >
                      {branch.name}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
};

export default BranchSelect;
